import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PublicClientApplication } from "@azure/msal-browser";
import { createMsalConfig, createLoginRequest } from "@/auth/msalConfig";
import type { TenantType } from "@/auth/azureAdConfig";
import { RouteUrl } from "@/configs/router/routes";
import { loginUser } from "@/api/user/user";
import { Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { usePostHog } from "@posthog/react";

const TENANT_STORAGE_KEY = "hockeyly_selected_tenant";
const AUTH_METHOD_KEY = "hockeyly_auth_method";

type LoginView = "choose" | "microsoft" | "credentials";

const LoginPage = () => {
    const [loginView, setLoginView] = useState<LoginView>("choose");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const posthog = usePostHog();

    useEffect(() => {
        const storedTenant = localStorage.getItem(TENANT_STORAGE_KEY);
        const storedAuthMethod = localStorage.getItem(AUTH_METHOD_KEY);
        if (storedTenant || storedAuthMethod === "token") {
            const redirectTo = sessionStorage.getItem("hockeyly_post_login_redirect");
            sessionStorage.removeItem("hockeyly_post_login_redirect");
            if (redirectTo) {
                window.location.href = redirectTo;
            } else {
                navigate({ to: RouteUrl.Dashboard });
            }
        }

        const loginError = sessionStorage.getItem("login_error");
        if (loginError) {
            setError(loginError);
            sessionStorage.removeItem("login_error");
        }
    }, [navigate]);

    const handleAzureLogin = async (tenant: TenantType) => {
        setLoading(true);
        setError(null);
        try {
            localStorage.setItem(TENANT_STORAGE_KEY, tenant);

            const config = createMsalConfig(tenant);
            const msalInstance = new PublicClientApplication(config);
            await msalInstance.initialize();
            const accounts = msalInstance.getAllAccounts();
            for (const account of accounts) {
                await msalInstance.clearCache({ account });
            }

            const tenantLoginRequest = createLoginRequest(tenant);
            await msalInstance.loginRedirect(tenantLoginRequest);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : String(err);

            if (errorMessage.includes("interaction_in_progress")) {
                setError("Logout is in progress. Please wait a moment before signing in again.");
            } else {
                setError(errorMessage);
            }

            setLoading(false);
            localStorage.removeItem(TENANT_STORAGE_KEY);
        }
    };

    const handleCredentialLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!username.trim() || !password.trim()) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await loginUser({ login: username, password });
            const resData = response.data;

            if (resData?.token) {
                localStorage.setItem("token", resData.token);
                localStorage.setItem(AUTH_METHOD_KEY, "token");
                posthog.identify(username, {
                    name: username,
                });
                const redirectTo = sessionStorage.getItem("hockeyly_post_login_redirect");
                sessionStorage.removeItem("hockeyly_post_login_redirect");
                window.location.href = redirectTo || "/dashboard";
            } else {
                setError("Login failed — no token received");
                setLoading(false);
            }
        } catch (err) {
            if (axios.isAxiosError(err)) {
                if (err.response) {
                    switch (err.response.status) {
                        case 400:
                            setError("Invalid request. Please check your inputs");
                            break;
                        case 401:
                            setError("Wrong username or password");
                            break;
                        case 403:
                            setError("Access denied");
                            break;
                        case 500:
                            setError("Server error. Please try again later");
                            break;
                        default:
                            setError(err.response.data?.message || "Unexpected error occurred");
                    }
                } else if (err.request) {
                    setError("No response from server. Please check your connection");
                }
            } else {
                setError("An unknown error occurred");
            }
            setLoading(false);
        }
    };

    const handleBack = () => {
        setLoginView("choose");
        setError(null);
        setUsername("");
        setPassword("");
    };

    return (
        <main className="min-h-screen flex flex-col md:flex-row bg-surface">
            {/* Left: Brand panel */}
            <section className="hidden md:flex md:w-1/2 performance-gradient relative overflow-hidden flex-col justify-between p-12">
                {/* Brand */}
                <div className="relative z-10">
                    <div className="text-2xl font-headline font-black tracking-tighter text-white uppercase">
                        Vålerenga Analytics
                    </div>
                    <div className="mt-4 h-1 w-12 bg-[#ba0a06]" />
                </div>

                {/* Headline */}
                <div className="relative z-10">
                    <h1 className="font-headline text-white text-5xl lg:text-7xl font-bold leading-tight tracking-tight uppercase">
                        The Performance
                        <br />
                        <span className="text-surface-container-highest">Architect.</span>
                    </h1>
                    <p className="mt-6 text-on-primary-container max-w-md text-lg font-light leading-relaxed">
                        Access real-time athletic intelligence and professional ice hockey data visualizations for
                        elite-level decision making.
                    </p>
                </div>

                {/* Stats */}
                <div className="relative z-10 flex gap-8">
                    <div className="flex flex-col">
                        <span className="text-[#ba0a06] font-headline font-bold text-3xl">24/7</span>
                        <span className="text-white text-[10px] font-headline uppercase tracking-widest opacity-60">
                            System Monitoring
                        </span>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[#ba0a06] font-headline font-bold text-3xl">100%</span>
                        <span className="text-white text-[10px] font-headline uppercase tracking-widest opacity-60">
                            Data Integrity
                        </span>
                    </div>
                </div>

                {/* Decorative background icon */}
                <div className="absolute -right-20 -bottom-20 opacity-10 pointer-events-none">
                    <span
                        className="material-symbols-outlined text-white"
                        style={{ fontSize: "40rem", fontVariationSettings: "'wght' 700" }}
                    >
                        monitoring
                    </span>
                </div>
            </section>

            {/* Right: Login form */}
            <section className="flex-grow md:w-1/2 bg-surface flex flex-col items-center justify-center p-6 md:p-12 overflow-y-auto">
                <div className="w-full max-w-md">
                    {/* Mobile brand header */}
                    <div className="md:hidden flex flex-col items-center mb-12">
                        <div className="text-2xl font-headline font-black tracking-tighter text-primary uppercase">
                            Vålerenga Analytics
                        </div>
                        <div className="mt-2 h-1 w-8 bg-[#ba0a06]" />
                    </div>

                    {/* Heading */}
                    <div className="mb-10">
                        <h2 className="font-headline text-3xl font-bold text-primary mb-2">Welcome Back</h2>
                        <p className="text-on-surface-variant text-sm">
                            Please select your preferred authentication method.
                        </p>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-6 px-4 py-3 rounded-md bg-error-container text-on-error-container text-sm">
                            {error}
                        </div>
                    )}

                    {/* Back button */}
                    {loginView !== "choose" && (
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={loading}
                            className="inline-flex items-center gap-2 text-[10px] font-headline font-bold text-on-surface-variant hover:text-primary uppercase tracking-widest mb-8 transition-colors disabled:opacity-50 cursor-pointer"
                        >
                            <span className="material-symbols-outlined text-base leading-none">arrow_back</span>
                            Back
                        </button>
                    )}

                    {/* View: choose */}
                    {loginView === "choose" && (
                        <div className="space-y-4">
                            <button
                                type="button"
                                onClick={() => setLoginView("microsoft")}
                                className="w-full flex items-center justify-center gap-3 py-3.5 px-6 bg-surface-container-lowest border border-outline-variant hover:bg-surface-container-low transition-colors rounded-md cursor-pointer"
                            >
                                <svg className="w-5 h-5 shrink-0" viewBox="0 0 21 21" fill="none">
                                    <rect width="10" height="10" fill="#F25022" />
                                    <rect x="11" width="10" height="10" fill="#7FBA00" />
                                    <rect y="11" width="10" height="10" fill="#00A4EF" />
                                    <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                                </svg>
                                <span className="font-headline font-semibold text-primary text-sm">
                                    Sign in with Microsoft
                                </span>
                            </button>

                            {/* Divider */}
                            <div className="relative flex items-center py-2">
                                <div className="flex-grow h-px bg-surface-container-highest" />
                                <span className="px-4 text-[10px] font-headline font-bold text-outline uppercase tracking-widest">
                                    OR
                                </span>
                                <div className="flex-grow h-px bg-surface-container-highest" />
                            </div>

                            <button
                                type="button"
                                onClick={() => setLoginView("credentials")}
                                className="w-full flex items-center justify-center py-3.5 px-6 bg-surface-container-highest hover:bg-surface-container-high transition-colors rounded-md cursor-pointer"
                            >
                                <span className="font-headline font-semibold text-on-surface text-sm uppercase tracking-widest">
                                    Sign in with Credentials
                                </span>
                            </button>
                        </div>
                    )}

                    {/* View: microsoft SSO */}
                    {loginView === "microsoft" && (
                        <div className="space-y-4">
                            <button
                                type="button"
                                onClick={() => handleAzureLogin("external")}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-3 py-3.5 px-6 bg-surface-container-lowest border border-outline-variant hover:bg-surface-container-low transition-colors rounded-md disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                            >
                                <svg className="w-5 h-5 shrink-0" viewBox="0 0 21 21" fill="none">
                                    <rect width="10" height="10" fill="#F25022" />
                                    <rect x="11" width="10" height="10" fill="#7FBA00" />
                                    <rect y="11" width="10" height="10" fill="#00A4EF" />
                                    <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                                </svg>
                                <span className="font-headline font-semibold text-primary text-sm">
                                    {loading ? "Signing in…" : "Microsoft SSO (Vålerenga User)"}
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={() => handleAzureLogin("internal")}
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-3 py-3.5 px-6 bg-surface-container-lowest border border-outline-variant hover:bg-surface-container-low transition-colors rounded-md disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                            >
                                <svg className="w-5 h-5 shrink-0" viewBox="0 0 21 21" fill="none">
                                    <rect width="10" height="10" fill="#F25022" />
                                    <rect x="11" width="10" height="10" fill="#7FBA00" />
                                    <rect y="11" width="10" height="10" fill="#00A4EF" />
                                    <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                                </svg>
                                <span className="font-headline font-semibold text-primary text-sm">
                                    {loading ? "Signing in…" : "Microsoft SSO (Jit User)"}
                                </span>
                            </button>
                        </div>
                    )}

                    {/* View: credentials */}
                    {loginView === "credentials" && (
                        <form className="space-y-6" onSubmit={handleCredentialLogin}>
                            <div>
                                <label
                                    htmlFor="username"
                                    className="block text-[10px] font-headline font-bold text-primary uppercase tracking-widest mb-2"
                                >
                                    Username
                                </label>
                                <input
                                    type="text"
                                    id="username"
                                    name="username"
                                    required
                                    placeholder="Enter your username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    disabled={loading}
                                    className="w-full bg-surface-container-highest rounded-md px-4 py-3 text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-high transition-all disabled:opacity-60"
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-[10px] font-headline font-bold text-primary uppercase tracking-widest mb-2"
                                >
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        required
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        disabled={loading}
                                        className="w-full bg-surface-container-highest rounded-md px-4 py-3 pr-12 text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary/20 focus:bg-surface-container-high transition-all disabled:opacity-60"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface transition-colors"
                                        tabIndex={-1}
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full performance-gradient text-white font-headline font-bold py-4 rounded-md shadow-lg shadow-primary/10 hover:opacity-90 active:scale-[0.98] transition-all uppercase tracking-widest text-sm disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                            >
                                {loading ? "Signing in…" : "Log In"}
                            </button>
                        </form>
                    )}

                    <p className="mt-10 text-center text-xs text-on-surface-variant">
                        Need an account?{" "}
                        <a className="text-primary font-bold hover:underline" href="#">
                            Contact System Administrator
                        </a>
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="absolute bottom-0 left-0 right-0 md:left-1/2 py-6 px-8 flex justify-center md:justify-end items-center gap-6">
                <span className="text-on-surface-variant text-xs opacity-60 font-body hidden md:block">
                    © 2024 Vålerenga Analytics
                </span>
            </footer>
        </main>
    );
};

export default LoginPage;
